import React, { useState, useEffect, useCallback } from 'react'
import { Eye, Edit, Trash2, Plus, Filter, X, ChevronDown, Search, RefreshCw } from 'lucide-react'
import { toast } from 'react-hot-toast'
import prePaymentService from '../../../services/prePaymentService'
import employeeService from '../../../services/employeeService'
import branchService from '../../../services/branchService'
import CreatePrePaymentModal from './CreatePrePaymentModal'
import EditPrePaymentModal from './EditPrePaymentModal'
import ViewPrePaymentModal from './ViewPrePaymentModal'

const PrePayments = () => {
  const [prePayments, setPrePayments] = useState([])
  const [filteredPrePayments, setFilteredPrePayments] = useState([])
  const [employees, setEmployees] = useState([])
  const [branches, setBranches] = useState([])
  
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  
  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [selectedPrePayment, setSelectedPrePayment] = useState(null)
  
  // Filter states
  const [filters, setFilters] = useState({
    employee: '',
    branch: '',
    month: '',
    search: ''
  })

  const fetchPrePayments = async () => {
    setIsLoading(true)
    setError('')
    
    try {
      const response = await prePaymentService.getAllPrePayments()
      
      if (response.success) {
        const prePaymentsData = response.data || []
        setPrePayments(prePaymentsData)
      } else {
        setError(response.message || 'Failed to fetch pre-payments')
      }
    } catch (error) {
      console.error('Error fetching pre-payments:', error)
      setError('Failed to fetch pre-payments')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchEmployees = async () => {
    try {
      const response = await employeeService.getAllEmployees()
      if (response.success) {
        const employeesData = response.data.employees || []
        setEmployees(Array.isArray(employeesData) ? employeesData.filter(emp => emp.isActive) : [])
      }
    } catch (error) {
      console.error('Error fetching employees:', error)
      setEmployees([]) // Ensure employees is always an array
    }
  }

  const fetchBranches = async () => {
    try {
      const response = await branchService.getAllBranches()
      if (response.success) {
        // Handle different possible response structures
        const branchesData = response.data?.branches || response.data || []
        setBranches(Array.isArray(branchesData) ? branchesData : [])
      }
    } catch (error) {
      console.error('Error fetching branches:', error)
      setBranches([]) // Ensure branches is always an array
    }
  }

  const applyFilters = useCallback(() => {
    let filtered = [...prePayments]

    // Employee filter
    if (filters.employee) {
      filtered = filtered.filter(prePayment => {
        const employeeId = prePayment.employee?._id || prePayment.employee
        return employeeId === filters.employee
      })
    }

    // Branch filter
    if (filters.branch) {
      filtered = filtered.filter(prePayment => {
        const branchId = prePayment.branch?._id || prePayment.employee?.branch?._id || prePayment.employee?.branch
        return branchId === filters.branch
      })
    }

    // Month filter
    if (filters.month) {
      filtered = filtered.filter(prePayment => prePayment.month === filters.month)
    }

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase()
      filtered = filtered.filter(prePayment =>
        (prePayment.employee?.fullName || '').toLowerCase().includes(searchTerm) ||
        (prePayment.note || '').toLowerCase().includes(searchTerm) ||
        (prePayment.amount || '').toString().includes(searchTerm)
      )
    }

    setFilteredPrePayments(filtered)
  }, [prePayments, filters])

  // Fetch data on component mount
  useEffect(() => {
    fetchPrePayments()
    fetchEmployees()
    fetchBranches()
  }, [])

  // Apply filters whenever applyFilters changes
  useEffect(() => {
    applyFilters()
  }, [applyFilters])

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const clearFilters = () => {
    setFilters({
      employee: '',
      branch: '',
      month: '',
      search: ''
    })
  }

  const hasActiveFilters = Object.values(filters).some(value => value !== '')

  const handleDelete = async (prePayment) => {
    if (window.confirm(`Are you sure you want to delete this pre-payment for ${prePayment.employee?.fullName}?`)) {
      try {
        const response = await prePaymentService.deletePrePayment(prePayment._id)
        
        if (response.success) {
          toast.success(response.message || 'Pre-payment deleted successfully', {
            style: {
              background: '#10B981',
              color: '#FFFFFF',
            },
          })
          fetchPrePayments()
        } else {
          toast.error(response.message || 'Failed to delete pre-payment')
        }
      } catch (error) {
        console.error('Error deleting pre-payment:', error)
        toast.error('Failed to delete pre-payment')
      }
    }
  }

  const handleModalSuccess = () => {
    fetchPrePayments()
  }

  const formatMonth = (monthStr) => {
    if (!monthStr) return 'N/A'
    
    // Handle both date objects and YYYY-MM strings
    const date = new Date(monthStr)
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      // Try parsing as YYYY-MM format
      const dateFromYearMonth = new Date(monthStr + '-01')
      if (isNaN(dateFromYearMonth.getTime())) {
        return 'N/A'
      }
      return dateFromYearMonth.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    }
    
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  }

  const formatCurrency = (amount) => {
    if (!amount) return '0.00'
    return parseFloat(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A'
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pre Payments</h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchPrePayments}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:bg-gray-100"
            title="Refresh Data"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Pre Payment
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-5 w-5 text-gray-400" />
          <h3 className="font-medium text-gray-900">Filters</h3>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="ml-auto flex items-center gap-1 text-orange-600 hover:text-orange-700 px-3 py-1 text-sm border border-orange-200 rounded-md hover:bg-orange-50 transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search pre-payments..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          </div>

          {/* Branch Filter */}
          <div className="relative">
            <select
              value={filters.branch}
              onChange={(e) => handleFilterChange('branch', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none bg-white"
            >
              <option value="">All Branches</option>
              {Array.isArray(branches) && branches.map((branch) => (
                <option key={branch._id} value={branch._id}>
                  {branch.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 pointer-events-none" />
          </div>

          {/* Employee Filter */}
          <div className="relative">
            <select
              value={filters.employee}
              onChange={(e) => handleFilterChange('employee', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none bg-white"
            >
              <option value="">All Employees</option>
              {Array.isArray(employees) && employees.map((employee) => (
                <option key={employee._id} value={employee._id}>
                  {employee.fullName}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 pointer-events-none" />
          </div>

          {/* Month Filter */}
          <div>
            <input
              type="month"
              value={filters.month}
              onChange={(e) => handleFilterChange('month', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Data Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {filteredPrePayments.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No pre-payments found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left p-4 font-medium text-gray-900">Date</th>
                  <th className="text-left p-4 font-medium text-gray-900">Employee</th>
                  <th className="text-left p-4 font-medium text-gray-900">Month</th>
                  <th className="text-left p-4 font-medium text-gray-900">Amount</th>
                  <th className="text-left p-4 font-medium text-gray-900">Note</th>
                  <th className="text-left p-4 font-medium text-gray-900">Branch</th>
                  <th className="text-right p-4 font-medium text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredPrePayments.map((prePayment) => (
                  <tr key={prePayment._id} className="hover:bg-gray-50">
                    <td className="p-4 text-gray-900">
                      {formatDate(prePayment.createdAt)}
                    </td>
                    <td className="p-4">
                      <div className="font-medium text-gray-900">
                        {prePayment.employee?.fullName || 'N/A'}
                      </div>
                    </td>
                    <td className="p-4 text-gray-600">
                      {formatMonth(prePayment.month)}
                    </td>
                    <td className="p-4">

                        {formatCurrency(prePayment.amount)}

                    </td>
                    <td className="p-4 text-gray-600">
                      <div className="max-w-xs truncate" title={prePayment.note}>
                        {prePayment.note || '-'}
                      </div>
                    </td>
                    <td className="p-4 text-gray-600">
                      {prePayment.branch?.name || prePayment.employee?.branch?.name || 'N/A'}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end">
                        <button
                          onClick={() => {
                            setSelectedPrePayment(prePayment)
                            setIsViewModalOpen(true)
                          }}
                          className="text-gray-400 hover:text-gray-600 p-1"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedPrePayment(prePayment)
                            setIsEditModalOpen(true)
                          }}
                          className="text-gray-400 hover:text-gray-600 p-1"
                          title="Edit Pre Payment"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(prePayment)}
                          className="text-gray-400 hover:text-gray-600 p-1"
                          title="Delete Pre Payment"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      <CreatePrePaymentModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleModalSuccess}
      />

      <EditPrePaymentModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={handleModalSuccess}
        prePayment={selectedPrePayment}
      />

      <ViewPrePaymentModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        prePayment={selectedPrePayment}
      />
    </div>
  )
}

export default PrePayments
